import React, { MouseEvent, ReactNode } from 'react';
import classNames from 'classnames';
import s from './Button.module.scss';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';

type BaseProps = {
  className?: string;
  children?: ReactNode;
  textSize?: 'b2' | 'b3' | 'b4' | 'mobile-b1' | 'mobile-b3';
  size?: 'xs' | 'sm' | 's' | 'm';
  width?: 'auto' | 'fill';
  variant?: 'fill' | 'outline' | 'ghost';
  color?: 'green' | 'red' | 'red-light';
  maxWidth?: string;
  height?: string;
  disabled?: boolean;
  multiline?: boolean;
};

type ButtonAsButton = BaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
    href?: never;
    type?: 'button' | 'submit' | 'reset';
  };

type ButtonAsAnchor = BaseProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

export const Button = (props: ButtonProps) => {
  const { isMobile } = useScreenDetector();
  const {
    className,
    children,
    disabled,
    textSize: initialTextSize,
    width = 'fill',
    variant = 'fill',
    maxWidth = 'none',
    color = 'green',
    size = 'm',
    height,
    multiline,
    ...rest
  } = props;

  const textSize = initialTextSize ?? (isMobile ? 'mobile-b1' : 'b3');

  const mods = {
    [s[`width-${width}`]]: true,
    [s[`variant-${variant}`]]: true,
    [s[`color-${color}`]]: true,
    [s[`size-${size}`]]: true,
    [s[textSize]]: !!textSize,
    [s.disabled]: disabled,
  };

  const childContent = multiline ? <span className={s.labelMultiline}>{children}</span> : children;

  if (props.href) {
    const { onClick, ...anchorRest } = rest as Omit<ButtonAsAnchor, keyof BaseProps>;

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <a
        className={classNames(s.Button, className, mods)}
        style={{ maxWidth, height }}
        onClick={handleClick}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
        {...anchorRest}
        href={props.href}
      >
        {childContent}
      </a>
    );
  }

  const { type = 'button', onClick, ...buttonRest } = rest as Omit<ButtonAsButton, keyof BaseProps>;

  return (
    <button
      type={type}
      className={classNames(s.Button, className, mods)}
      style={{ maxWidth, height }}
      onClick={onClick}
      disabled={disabled}
      {...buttonRest}
    >
      {childContent}
    </button>
  );
};
